import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, RefreshCw, Send, Paperclip, PenSquare, FileText, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errText, API } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Badge, EMAIL_STATUS, TemplateSelect, card, inputCls, btnPrimary, btnGhost, fmtDateTime } from "./common";

function Compose({ templates, onClose, onSent, presetTo }) {
  const [to, setTo] = useState(presetTo || "");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await api.post("/admin/emails/send", { to, subject, body }); toast.success("E-Mail gesendet"); onSent(); }
    catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };
  return (
    <div className={`${card} p-5 space-y-3`} data-testid="email-compose">
      <p className="font-bold text-sm">Neue E-Mail</p>
      <input className={inputCls} placeholder="Empfänger (E-Mail)" value={to} onChange={(e) => setTo(e.target.value)} data-testid="compose-to-input" />
      <input className={inputCls} placeholder="Betreff" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="compose-subject-input" />
      <TemplateSelect templates={templates} onPick={(t) => { t.subject && setSubject(t.subject); setBody(t.body); }} />
      <textarea className={`${inputCls} min-h-[140px]`} placeholder="Nachricht…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="compose-body-input" />
      <div className="flex gap-2 justify-end">
        <button className={btnGhost} onClick={onClose}>Abbrechen</button>
        <button className={btnPrimary} disabled={busy || !to || !subject || !body} onClick={send} data-testid="compose-send-btn">Senden</button>
      </div>
    </div>
  );
}

function Templates({ templates, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [preview, setPreview] = useState(null);
  const [name, setName] = useState("");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const save = async () => {
    try { await api.post("/admin/email-templates", { name, subject, body }); toast.success("Vorlage gespeichert"); setName(""); setSubject(""); setBody(""); setShowForm(false); reload(); }
    catch (e) { toast.error(errText(e)); }
  };
  const groups = {};
  templates.forEach((t) => {
    const g = t.name.includes("–") ? t.name.split("–")[0].trim() : "Weitere";
    (groups[g] = groups[g] || []).push(t);
  });
  return (
    <div className={`${card} p-5`} data-testid="email-templates">
      <div className="flex items-center mb-4">
        <p className="font-bold text-sm">E-Mail-Vorlagen <span className="text-muted-foreground font-normal">({templates.length})</span></p>
        <button className={`${btnGhost} ml-auto !py-1.5 !text-xs`} onClick={() => setShowForm(!showForm)} data-testid="template-new-btn">+ Neue Vorlage</button>
      </div>
      {showForm && (
        <div className="border border-border rounded-xl p-3.5 mb-4 space-y-2">
          <div className="grid sm:grid-cols-2 gap-2">
            <input className={inputCls} placeholder="Name (z. B. Konto – Thema)" value={name} onChange={(e) => setName(e.target.value)} data-testid="template-name-input" />
            <input className={inputCls} placeholder="Betreff (optional)" value={subject} onChange={(e) => setSubject(e.target.value)} />
          </div>
          <textarea className={`${inputCls} min-h-[80px]`} placeholder="Vorlagentext…" value={body} onChange={(e) => setBody(e.target.value)} data-testid="template-body-input" />
          <button className={`${btnGhost} !text-xs`} disabled={!name || !body} onClick={save} data-testid="template-save-btn">Speichern</button>
        </div>
      )}
      <div className="space-y-4">
        {Object.entries(groups).map(([g, items]) => (
          <div key={g}>
            <p className="text-[10px] font-black tracking-[0.15em] uppercase text-muted-foreground mb-1.5">{g}</p>
            <div className="divide-y divide-border/60 border border-border rounded-xl overflow-hidden">
              {items.map((t) => (
                <div key={t.id} className="bg-white">
                  <div className="flex items-center gap-2 px-3 py-2">
                    <button className="flex-1 min-w-0 text-left" onClick={() => setPreview(preview === t.id ? null : t.id)} data-testid={`template-row-${t.id}`}>
                      <span className="block text-xs font-bold truncate">{t.name.includes("–") ? t.name.split("–").slice(1).join("–").trim() : t.name}</span>
                      {t.subject && <span className="block text-[11px] text-muted-foreground truncate">{t.subject}</span>}
                    </button>
                    <button className="text-[11px] font-semibold text-muted-foreground hover:text-foreground shrink-0" onClick={() => setPreview(preview === t.id ? null : t.id)}>{preview === t.id ? "Schließen" : "Vorschau"}</button>
                    <button className="text-[11px] font-semibold text-red-500 hover:underline shrink-0"
                      onClick={async () => { if (!window.confirm("Vorlage wirklich löschen?")) return; try { await api.delete(`/admin/email-templates/${t.id}`); reload(); } catch (e) { toast.error(errText(e)); } }}>Löschen</button>
                  </div>
                  {preview === t.id && <p className="text-[11px] text-foreground/75 whitespace-pre-wrap bg-secondary/40 px-3 py-2.5 border-t border-border/60">{t.body}</p>}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EmailDetail({ id, onBack, templates, reloadList }) {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => api.get(`/admin/emails/${id}`).then(({ data }) => setData(data)).catch((e) => toast.error(errText(e))), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="h-60 bg-muted rounded-2xl animate-pulse" />;
  const e = data.email;
  return (
    <div className="space-y-4" data-testid="email-detail">
      <button onClick={() => { onBack(); reloadList(); }} className="inline-flex items-center gap-1.5 text-sm font-semibold text-muted-foreground hover:text-foreground"><ArrowLeft className="w-4 h-4" /> Zurück</button>
      <div className={`${card} p-5`}>
        <div className="flex flex-wrap items-center gap-2">
          <Badge map={EMAIL_STATUS} value={e.status} />
          <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${e.direction === "IN" ? "bg-blue-100 text-blue-700" : "bg-secondary text-foreground/70"}`}>{e.direction === "IN" ? "Eingehend" : "Gesendet"}</span>
          {e.send_error && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-700">Fehler: {e.send_error}</span>}
          <span className="text-xs text-muted-foreground ml-auto">{fmtDateTime(e.date)}</span>
        </div>
        <h2 className="font-bold text-lg mt-2">{e.subject || "(Kein Betreff)"}</h2>
        <p className="text-sm text-muted-foreground">Von: {e.from_name ? `${e.from_name} <${e.from_addr}>` : e.from_addr} · An: {e.to_addr}</p>
        {data.ticket && <p className="text-xs mt-1">Verknüpftes Ticket: <span className="font-mono font-bold">{data.ticket.ticket_id}</span> – {data.ticket.subject}</p>}
        <div className="flex flex-wrap gap-2 mt-3">
          <select className="text-sm border border-border rounded-full px-3 py-1.5 bg-white" value={e.status} data-testid="email-status-select"
            onChange={async (ev) => { try { await api.patch(`/admin/emails/${id}`, { status: ev.target.value }); load(); } catch (er) { toast.error(errText(er)); } }}>
            {Object.entries(EMAIL_STATUS).map(([k, [l]]) => <option key={k} value={k}>{l}</option>)}
          </select>
          {e.direction === "IN" && !e.ticket_doc_id && (
            <button className={btnGhost} data-testid="email-make-ticket-btn"
              onClick={async () => { try { const { data: r } = await api.post(`/admin/emails/${id}/make-ticket`, { category: "ALLGEMEIN" }); toast.success(`Ticket ${r.ticket_id} erstellt`); load(); } catch (er) { toast.error(errText(er)); } }}>
              Als Ticket übernehmen
            </button>
          )}
          {user?.role === "SUPER_ADMIN" && (
            <button className="inline-flex items-center gap-1.5 text-xs font-bold text-red-600 border border-red-200 bg-red-50 hover:bg-red-100 px-4 py-2 rounded-full" data-testid="email-delete-btn"
              onClick={async () => {
                if (!window.confirm("Diese E-Mail endgültig aus dem Panel löschen?")) return;
                try { await api.delete(`/admin/emails/${id}`); toast.success("E-Mail gelöscht"); onBack(); reloadList(); } catch (er) { toast.error(errText(er)); }
              }}>
              <Trash2 className="w-3.5 h-3.5" /> Löschen
            </button>
          )}
        </div>
        <div className="mt-4 border-t border-border pt-4">
          {e.html ? (
            <iframe title="email" sandbox="" srcDoc={e.html} className="w-full min-h-[320px] bg-white rounded-xl border border-border" />
          ) : (
            <p className="text-sm whitespace-pre-wrap">{e.text || "(Kein Inhalt)"}</p>
          )}
          {(e.attachments || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-3">
              {e.attachments.map((a) => (
                <a key={a.id} href={`${API}/admin/emails/${id}/attachments/${a.id}`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-semibold border border-border rounded-full px-3 py-1.5 hover:bg-secondary/60">
                  <Paperclip className="w-3.5 h-3.5" /> {a.filename} ({Math.round(a.size / 1024)} KB)
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
      {e.direction === "IN" && (
        <div className={`${card} p-5 space-y-3`}>
          <div className="flex items-center">
            <p className="font-bold text-sm">Antworten</p>
            <span className="ml-auto"><TemplateSelect templates={templates} onPick={(t) => setReply(t.body)} /></span>
          </div>
          <textarea className={`${inputCls} min-h-[120px]`} value={reply} onChange={(ev) => setReply(ev.target.value)} placeholder="Deine Antwort…" data-testid="email-reply-input" />
          <button className={`${btnPrimary} inline-flex items-center gap-1.5`} disabled={busy || !reply.trim()} data-testid="email-reply-btn"
            onClick={async () => { setBusy(true); try { await api.post(`/admin/emails/${id}/reply`, { body: reply }); toast.success("Antwort gesendet"); setReply(""); load(); } catch (er) { toast.error(errText(er)); } finally { setBusy(false); } }}>
            <Send className="w-3.5 h-3.5" /> Antwort senden
          </button>
        </div>
      )}
    </div>
  );
}

export default function PanelEmails() {
  const [res, setRes] = useState(null);
  const [dir, setDir] = useState("IN");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState("list");
  const [templates, setTemplates] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const loadTpl = useCallback(() => api.get("/admin/email-templates").then(({ data }) => setTemplates(data.templates)).catch(() => {}), []);
  const load = useCallback(() => {
    const p = new URLSearchParams({ page, direction: dir });
    q && p.set("q", q);
    api.get(`/admin/emails?${p}`).then(({ data }) => setRes(data)).catch((e) => toast.error(errText(e)));
  }, [dir, q, page]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadTpl(); }, [loadTpl]);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/admin/emails/sync"); toast.success(`${data.fetched} neue E-Mail(s) abgerufen`); load(); }
    catch (e) { toast.error(errText(e)); } finally { setSyncing(false); }
  };

  if (selected) return <EmailDetail id={selected} templates={templates} onBack={() => setSelected(null)} reloadList={load} />;
  return (
    <div className="space-y-4" data-testid="panel-emails">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex bg-secondary/80 rounded-full p-1">
          {[["IN", "Posteingang"], ["OUT", "Gesendet"]].map(([k, l]) => (
            <button key={k} onClick={() => { setDir(k); setPage(1); setView("list"); }} data-testid={`emails-dir-${k}`}
              className={`text-xs font-bold px-4 py-2 rounded-full ${dir === k && view === "list" ? "bg-[#111111] text-white" : "text-foreground/60"}`}>{l}</button>
          ))}
        </div>
        <input className={`${inputCls} max-w-xs`} placeholder="Suchen…" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} data-testid="emails-search-input" />
        <div className="ml-auto flex gap-2">
          <button className={`${btnGhost} inline-flex items-center gap-1.5`} onClick={() => setView(view === "templates" ? "list" : "templates")} data-testid="emails-templates-btn"><FileText className="w-3.5 h-3.5" /> Vorlagen</button>
          <button className={`${btnGhost} inline-flex items-center gap-1.5`} disabled={syncing} onClick={sync} data-testid="emails-sync-btn"><RefreshCw className={`w-3.5 h-3.5 ${syncing ? "animate-spin" : ""}`} /> Abrufen</button>
          <button className={`${btnPrimary} inline-flex items-center gap-1.5`} onClick={() => setView("compose")} data-testid="emails-compose-btn"><PenSquare className="w-3.5 h-3.5" /> Neue E-Mail</button>
        </div>
      </div>
      {res && !res.configured && dir === "IN" && (
        <p className="text-xs bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-4 py-3">Kein IMAP-Posteingang konfiguriert. Eingehende Mails können nicht abgerufen werden – Versand funktioniert.</p>
      )}
      {view === "compose" && <Compose templates={templates} onClose={() => setView("list")} onSent={() => { setView("list"); setDir("OUT"); load(); }} />}
      {view === "templates" && <Templates templates={templates} reload={loadTpl} />}
      <div className={`${card} overflow-hidden`}>
        <div className="divide-y divide-border/60" data-testid="emails-list">
          {!res && <p className="px-4 py-8 text-center text-muted-foreground text-sm">Lade…</p>}
          {res?.emails.length === 0 && <p className="px-4 py-8 text-center text-muted-foreground text-sm">Keine E-Mails.</p>}
          {res?.emails.map((e) => (
            <button key={e.id} onClick={() => setSelected(e.id)} className={`w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-secondary/40 ${!e.read && e.direction === "IN" ? "bg-accent/5" : ""}`} data-testid={`email-row-${e.id}`}>
              {!e.read && e.direction === "IN" && <span className="w-2 h-2 rounded-full bg-accent shrink-0" />}
              <span className="flex-1 min-w-0">
                <span className={`block text-sm truncate ${!e.read && e.direction === "IN" ? "font-bold" : "font-semibold"}`}>{e.subject || "(Kein Betreff)"}</span>
                <span className="block text-xs text-muted-foreground truncate">{e.direction === "IN" ? e.from_addr : `an ${e.to_addr}`}{e.text ? ` – ${e.text}` : ""}</span>
              </span>
              {e.ticket_doc_id && <span className="text-[10px] font-bold bg-secondary px-2 py-0.5 rounded-full shrink-0">Ticket</span>}
              <Badge map={EMAIL_STATUS} value={e.status} />
              <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">{fmtDateTime(e.date)}</span>
            </button>
          ))}
        </div>
        {res && res.pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-border text-sm">
            <button className={btnGhost} disabled={page <= 1} onClick={() => setPage(page - 1)}>Zurück</button>
            <span className="text-xs text-muted-foreground">Seite {res.page} von {res.pages}</span>
            <button className={btnGhost} disabled={page >= res.pages} onClick={() => setPage(page + 1)}>Weiter</button>
          </div>
        )}
      </div>
    </div>
  );
}
